/**
 * BoundResolver — runtime binding + policy resolution with per-agent isolation.
 *
 * Resolves artifact binding and guardrail/policy in one pass at runtime,
 * producing a per-agent scoped ResolvedComponent. Reuses existing
 * resolveBound(), buildGuardrailContext(), and agent scope resolution.
 */

import { readFile } from "node:fs/promises";
import YAML from "yaml";
import { buildGuardrailContext, type GuardrailCheckEntry, type GuardrailContext } from "../generator/context.js";
import { buildRegistriesFromDsl } from "./dsl-context.js";
import type { GuardrailRuleData } from "./guardrail-hooks.js";
import type { WorkflowRegistries } from "./workflow-runner.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ArtifactBindingConfig {
  source: string;
  mappings?: Record<string, string>;
}

export interface BoundResolveOptions {
  /** Component identifier — used for cache keying. */
  id: string;
  /** Pre-resolved DSL (e.g. embedded from codegen). */
  embeddedDsl?: Record<string, unknown>;
  /** Path to DSL file — resolved via agent-contracts when no embeddedDsl. */
  dslPath?: string;
  /** Paths to software binding YAML files (guardrail_impl, etc.). */
  bindingPaths?: string[];
  /** Active guardrail policy name from DSL guardrail_policies. */
  activeGuardrailPolicy?: string;
  /** Artifact binding config — resolved via agent-contracts resolveBound(). */
  artifactBinding?: ArtifactBindingConfig;
  /** Path substitutions for artifact binding. */
  paths?: Record<string, string>;
  /** Path to toolchain-contracts.yaml. */
  toolchainContractsPath?: string;
}

export interface ResolvedToolchain {
  id: string;
  kind: "library" | "cli" | "component";
  description?: string;
  module?: string;
  command?: string;
  cli_contract?: string;
  component_contract?: string;
  artifacts: { consumes: string[]; produces: string[] };
  operations?: string[];
  source: "toolchain-contracts" | "agent-contracts" | "merged";
}

export interface ResolvedComponent {
  id: string;
  dsl: Record<string, unknown>;
  registries: WorkflowRegistries;
  guardrails: {
    perAgent: Map<string, GuardrailRuleData>;
  };
  diagnostics: Array<{ path?: string; message: string; severity?: string }>;
  toolchains?: Record<string, ResolvedToolchain>;
  toolchainsByAgent?: Map<string, ResolvedToolchain[]>;
}

// ---------------------------------------------------------------------------
// Guardrail scope helpers
// ---------------------------------------------------------------------------

/**
 * Effective guardrail IDs for an agent: entity-side bindings ∪ scope-side bindings.
 */
export function getEffectiveGuardrailIds(
  dsl: Record<string, unknown>,
  agentId: string,
): Set<string> {
  const ids = new Set<string>();

  const agents = (dsl.agents ?? {}) as Record<string, Record<string, unknown>>;
  const agent = agents[agentId];
  if (agent) {
    for (const ref of (agent.guardrails as string[]) ?? []) {
      ids.add(ref);
    }
  }

  const guardrails = (dsl.guardrails ?? {}) as Record<
    string,
    { scope?: { agents?: string[] } }
  >;
  for (const [guardrailId, guardrail] of Object.entries(guardrails)) {
    if (guardrail.scope?.agents?.includes(agentId)) {
      ids.add(guardrailId);
    }
  }

  return ids;
}

function guardrailCheckToCommandRule(c: GuardrailCheckEntry) {
  return {
    guardrail_id: c.guardrail_id,
    pattern: c.pattern,
    action: c.action as GuardrailRuleData["commandRules"][number]["action"],
    message: c.message,
  };
}

function guardrailCheckToFileRule(c: GuardrailCheckEntry) {
  return {
    guardrail_id: c.guardrail_id,
    pattern: c.pattern,
    action: c.action as GuardrailRuleData["fileRules"][number]["action"],
    message: c.message,
    ...(c.exclude_glob ? { exclude_glob: c.exclude_glob } : {}),
  };
}

function guardrailCheckToContentRule(c: GuardrailCheckEntry) {
  return {
    guardrail_id: c.guardrail_id,
    pattern: c.pattern,
    action: c.action as GuardrailRuleData["contentRules"][number]["action"],
    message: c.message,
    ...(c.file_glob ? { file_glob: c.file_glob } : {}),
    ...(c.exclude_glob ? { exclude_glob: c.exclude_glob } : {}),
  };
}

export function guardrailContextToRuleData(
  ctx: GuardrailContext,
  effectiveIds?: Set<string>,
): GuardrailRuleData {
  const filter = (checks: GuardrailCheckEntry[]) =>
    effectiveIds ? checks.filter((c) => effectiveIds.has(c.guardrail_id)) : checks;

  return {
    commandRules: filter(ctx.commandChecks).map(guardrailCheckToCommandRule),
    fileRules: filter(ctx.fileChecks).map(guardrailCheckToFileRule),
    contentRules: filter(ctx.contentChecks).map(guardrailCheckToContentRule),
  };
}

export function buildPerAgentGuardrails(
  dsl: Record<string, unknown>,
  guardrailCtx: GuardrailContext,
): Map<string, GuardrailRuleData> {
  const perAgent = new Map<string, GuardrailRuleData>();
  const agents = (dsl.agents ?? {}) as Record<string, unknown>;

  for (const agentId of Object.keys(agents)) {
    const effectiveIds = getEffectiveGuardrailIds(dsl, agentId);
    const rules = guardrailContextToRuleData(guardrailCtx, effectiveIds);
    if (
      rules.commandRules.length > 0 ||
      rules.fileRules.length > 0 ||
      rules.contentRules.length > 0
    ) {
      perAgent.set(agentId, rules);
    }
  }

  return perAgent;
}

function hasGuardrailRules(rules: GuardrailRuleData): boolean {
  return (
    rules.commandRules.length > 0 ||
    rules.fileRules.length > 0 ||
    rules.contentRules.length > 0
  );
}

// ---------------------------------------------------------------------------
// Toolchain derivation — types for dynamic import from @aaac/toolchain
// ---------------------------------------------------------------------------

interface ToolchainDSLEntry {
  kind: "library" | "cli" | "component";
  description?: string;
  module?: string;
  command?: string;
  cli_contract?: string;
  component_contract?: string;
  artifacts: { consumes: string[]; produces: string[] };
  operations?: string[];
}

interface ToolchainContractsDSLShape {
  toolchains: Record<string, ToolchainDSLEntry>;
}

interface ToolchainModule {
  loadToolchainContracts(filePath: string): Promise<ToolchainContractsDSLShape>;
}

function mergeDerivedWithAgentTools(
  derivedTools: ResolvedToolchain[],
  agentToolIds: string[],
  allToolchains: Record<string, ResolvedToolchain>,
): ResolvedToolchain[] {
  const merged = new Map<string, ResolvedToolchain>();

  for (const tc of derivedTools) {
    merged.set(tc.id, tc);
  }

  for (const toolId of agentToolIds) {
    if (!merged.has(toolId)) {
      if (allToolchains[toolId]) {
        merged.set(toolId, { ...allToolchains[toolId], source: "merged" });
      }
    } else {
      const existing = merged.get(toolId)!;
      merged.set(toolId, { ...existing, source: "merged" });
    }
  }

  return Array.from(merged.values());
}

export function deriveToolchainsForAgents(
  dsl: Record<string, unknown>,
  toolchains: Record<string, ResolvedToolchain>,
): Map<string, ResolvedToolchain[]> {
  const result = new Map<string, ResolvedToolchain[]>();
  const agents = (dsl.agents ?? {}) as Record<string, Record<string, unknown>>;

  for (const [agentId, agent] of Object.entries(agents)) {
    const derivedTools: ResolvedToolchain[] = [];

    const canReadArtifacts = (agent.can_read_artifacts ?? []) as string[];
    const canWriteArtifacts = (agent.can_write_artifacts ?? []) as string[];
    const agentArtifacts = agent.artifacts as
      | { produces?: string[]; consumes?: string[] }
      | undefined;
    const readArtifacts = new Set([
      ...canReadArtifacts,
      ...(agentArtifacts?.consumes ?? []),
    ]);
    const writeArtifacts = new Set([
      ...canWriteArtifacts,
      ...(agentArtifacts?.produces ?? []),
    ]);

    for (const tc of Object.values(toolchains)) {
      let matched = false;

      for (const artifactId of tc.artifacts.consumes) {
        if (readArtifacts.has(artifactId) || writeArtifacts.has(artifactId)) {
          matched = true;
          break;
        }
      }

      if (!matched) {
        for (const artifactId of tc.artifacts.produces) {
          if (writeArtifacts.has(artifactId)) {
            matched = true;
            break;
          }
        }
      }

      if (matched) {
        derivedTools.push(tc);
      }
    }

    if (derivedTools.length > 0) {
      const agentTools = (agent.can_execute_tools ?? []) as string[];
      const mergedTools = mergeDerivedWithAgentTools(
        derivedTools,
        agentTools,
        toolchains,
      );
      result.set(agentId, mergedTools);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// BoundResolver
// ---------------------------------------------------------------------------

export class BoundResolver {
  private readonly cache = new Map<string, ResolvedComponent>();

  /**
   * Resolve binding + policy in one pass. Results are cached by component id.
   */
  async resolve(opts: BoundResolveOptions): Promise<ResolvedComponent> {
    const cached = this.cache.get(opts.id);
    if (cached) return cached;

    const diagnostics: ResolvedComponent["diagnostics"] = [];
    let dsl: Record<string, unknown>;

    if (opts.embeddedDsl) {
      dsl = opts.embeddedDsl;
    } else if (opts.dslPath) {
      const { resolve: resolveDsl } = await import("agent-contracts");
      const result = await resolveDsl(opts.dslPath);
      dsl = result.data;
    } else {
      throw new Error("BoundResolver.resolve requires embeddedDsl or dslPath");
    }

    // Artifact binding (resolveBound)
    if (opts.artifactBinding) {
      const { resolveBound } = await import("agent-contracts");
      const boundResult = await resolveBound(dsl, {
        artifactBinding: opts.artifactBinding,
        paths: opts.paths,
      });
      dsl = boundResult.data;
      for (const d of boundResult.diagnostics) {
        diagnostics.push({
          path: d.rule,
          message: d.message,
          severity: d.severity,
        });
      }
    }

    // Guardrail + policy from bindings
    let guardrailCtx: GuardrailContext = {
      commandChecks: [],
      fileChecks: [],
      contentChecks: [],
      allChecks: [],
      hasGuardrails: false,
    };

    if (opts.bindingPaths && opts.bindingPaths.length > 0) {
      for (const bindingPath of opts.bindingPaths) {
        const raw = await readFile(bindingPath, "utf8");
        const binding = YAML.parse(raw) as Record<string, unknown>;
        guardrailCtx = buildGuardrailContext(
          dsl,
          binding,
          opts.activeGuardrailPolicy,
        );
      }
    }

    const perAgent = guardrailCtx.hasGuardrails
      ? buildPerAgentGuardrails(dsl, guardrailCtx)
      : new Map<string, GuardrailRuleData>();

    let toolchains: Record<string, ResolvedToolchain> | undefined;
    let toolchainsByAgent: Map<string, ResolvedToolchain[]> | undefined;

    if (opts.toolchainContractsPath) {
      const toolchainPkg = "@aaac/toolchain";
      const mod = (await import(/* webpackIgnore: true */ toolchainPkg)) as ToolchainModule;
      const tcDoc = await mod.loadToolchainContracts(opts.toolchainContractsPath);

      toolchains = {};
      for (const [id, tc] of Object.entries(tcDoc.toolchains) as Array<[string, ToolchainDSLEntry]>) {
        toolchains[id] = {
          id,
          kind: tc.kind,
          description: tc.description,
          module: tc.module,
          command: tc.command,
          cli_contract: tc.cli_contract,
          component_contract: tc.component_contract,
          artifacts: {
            consumes: [...tc.artifacts.consumes],
            produces: [...tc.artifacts.produces],
          },
          operations: tc.operations ? [...tc.operations] : undefined,
          source: "toolchain-contracts",
        };
      }

      toolchainsByAgent = deriveToolchainsForAgents(dsl, toolchains);
    }

    const component: ResolvedComponent = {
      id: opts.id,
      dsl,
      registries: buildRegistriesFromDsl(dsl),
      guardrails: { perAgent },
      diagnostics,
      ...(toolchains ? { toolchains } : {}),
      ...(toolchainsByAgent ? { toolchainsByAgent } : {}),
    };

    this.cache.set(opts.id, component);
    return component;
  }

  /** Clear cached components (e.g. for testing). */
  clearCache(): void {
    this.cache.clear();
  }
}

/**
 * Get guardrail rules for a specific agent from a ResolvedComponent.
 * Falls back to embedded _guardrailRules when no per-agent rules exist.
 */
export function getGuardrailRulesForAgent(
  component: ResolvedComponent,
  agentId: string,
  fallbackDsl?: Record<string, unknown>,
): GuardrailRuleData | undefined {
  const perAgent = component.guardrails.perAgent.get(agentId);
  if (perAgent && hasGuardrailRules(perAgent)) {
    return perAgent;
  }

  if (fallbackDsl) {
    const raw = fallbackDsl._guardrailRules as Record<string, unknown> | undefined;
    if (raw) {
      return {
        commandRules: (raw.commandRules as GuardrailRuleData["commandRules"]) ?? [],
        fileRules: (raw.fileRules as GuardrailRuleData["fileRules"]) ?? [],
        contentRules: (raw.contentRules as GuardrailRuleData["contentRules"]) ?? [],
      };
    }
  }

  return undefined;
}
